import { calculateDose } from './RecUtil';
import { normalizeTestTime } from './DrawGroupUtil';
import { markerKey } from './labMerge';
import supplementsCatalog from '../../lambda/health-recommendations/supplements.json';
import { RECOMMENDATION_DISPLAY } from '../config/recommendationDisplay';

// ── Era-dated file naming ────────────────────────────────────────────
// protocol_<YYYYMMDD>.json / compliance_<YYYYMMDD>.json where the date is the
// protocol GENERATION (era) date. Edits update the same era file in place; a new
// era file appears only when a new protocol is generated. Plain protocol.json /
// compliance.json are legacy read-only fallbacks. retrieve-user-files returns at
// most one of each, so finders take the first match.

export const PROTOCOL_FILE_RE = /^protocol(_\d{8})?\.json$/;
export const COMPLIANCE_FILE_RE = /^compliance(_\d{8})?\.json$/;

/**
 * Era date (YYYYMMDD) for a protocol's generatedAt timestamp, in the USER'S
 * LOCAL date — NOT the UTC date. Compliance day keys are local dates; deriving
 * the era from the UTC ISO slice mints "tomorrow's" era during local evenings
 * (after the UTC rollover), which orphans the day's compliance from its era file.
 */
export function protocolEraDate(generatedAt) {
    const d = generatedAt ? new Date(generatedAt) : new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/**
 * Find the live protocol file in a userFiles list.
 * Era-dated files are primary — the newest era (max filename date) wins; plain
 * protocol.json is only the legacy fallback. This must not rely on array order:
 * until the backend filters to a single file (and for stale caches/transition
 * states), the list can contain the plain file AND dated files together, and
 * S3 list order puts 'protocol.json' before 'protocol_YYYYMMDD.json'.
 */
export function findProtocolFile(files) {
    const all = (files || []).filter(f => PROTOCOL_FILE_RE.test(f.key));
    if (all.length === 0) return null;
    const dated = all.filter(f => /^protocol_\d{8}\.json$/.test(f.key));
    if (dated.length === 0) return all[0];
    return dated.reduce((a, b) => (a.key >= b.key ? a : b)); // max era date
}

/** Find the compliance file in a userFiles list (era-dated or legacy plain). */
export function findComplianceFile(files) {
    return (files || []).find(f => COMPLIANCE_FILE_RE.test(f.key)) || null;
}

/**
 * Compute a fingerprint from the current blood test files.
 * Changes when a draw is added, removed, or gains a marker.
 *
 * Keyed on what each draw CONTAINS rather than on the S3 keys behind it, because
 * the automatic merge rewrites those keys without changing a single result. A
 * key-based fingerprint therefore flipped `canRegenerate` four seconds after any
 * fragmented draw loaded and told the patient "Newer lab results available" when
 * nothing had arrived — the merge is supposed to be invisible. Adding a panel
 * sheet to an existing draw still moves the fingerprint, because that brings new
 * marker names with it.
 *
 * Both components are read through the same resolvers the merge uses, because
 * the two sides spell an unchanged draw differently otherwise:
 *
 *   time - groupFilesByDraw rewrites header.testtime to the NORMALIZED value for
 *     a multi-file draw (DrawGroupUtil buildGroup), while mergeDrawGroup keeps
 *     the RAW one. "00:00" is the ingest placeholder for "no collection time",
 *     so the common untimed draw fingerprinted as "@" before a merge and
 *     "@00:00" after it - a difference in every draw whose PDF carried no time.
 *
 *   markers - two panel sheets can spell one marker differently. Grouping keeps
 *     the first-seen spelling (by key sort) and the merge keeps the winning
 *     source's (by write time), so raw aliases moved the fingerprint across a
 *     merge that changed no result. Resolving through normalizeTestName collapses
 *     both to the canonical name; de-duplicating matches what both paths store.
 *
 * Without either, canRegenerate flipped four seconds after a fragmented draw
 * loaded and told the patient "Newer lab results available" - which is the exact
 * failure the content-based fingerprint was introduced to remove.
 *
 * @param {Array} bloodTestFiles - grouped draws (each has .testdate/.testtime and
 *        .data.body; a plain file object also works)
 * @param {Function} [normalizeTestName] - canonical-name resolver. Pass the same
 *        one the grouping and merge use; omitting it leaves aliases raw, which is
 *        stable but not merge-invariant.
 * @returns {string|null} Stable digest of the draw set, or null if empty
 */
export function computeBloodTestFingerprint(bloodTestFiles, normalizeTestName) {
    if (!bloodTestFiles || !bloodTestFiles.length) return null;
    return bloodTestFiles
        .map(f => {
            const date = f.testdate ?? f.data?.header?.testdate ?? 'nodate';
            const time = normalizeTestTime(f.testtime ?? f.data?.header?.testtime) || '';
            const markers = [...new Set((f.data?.body || [])
                .map(e => markerKey(e, normalizeTestName))
                .filter(Boolean))]
                .sort()
                .join('+');
            return `${date}@${time}:${markers}`;
        })
        .sort()
        .join(',');
}

/**
 * The key-based fingerprint saved protocols used before the content-based one.
 *
 * Kept so an existing protocol is not marked stale the first time this code runs:
 * without it, every user with a saved protocol would see the regenerate prompt
 * once, for a change that altered nothing about their labs.
 */
export function legacyBloodTestFingerprint(bloodTestFiles) {
    if (!bloodTestFiles || !bloodTestFiles.length) return null;
    return bloodTestFiles
        .flatMap(f => (f.sourceKeys?.length ? f.sourceKeys : [f.key]))
        .sort()
        .join(',');
}

/**
 * Group supplements by primary category with weights, sorted by weight descending.
 * Shared by the protocol editor (display) and the auto-save path so the saved
 * protocol always matches what the user sees.
 *
 * @param {Array} supplements - recommendations.supplements from the engine
 * @returns {Array} Sorted array of { category, weight, products } groups
 */
export function groupSupplementsByCategory(supplements) {
    if (!supplements || !supplements.length) return [];

    const groupMap = new Map();
    supplements.forEach(supp => {
        const primaryCat = supp.fulfills_categories?.[0]?.category
            || supp.category || 'uncategorized';
        const categoryWeight = supp.fulfills_categories?.[0]?.weight || supp.total_weight || 0;
        if (!groupMap.has(primaryCat)) {
            groupMap.set(primaryCat, { category: primaryCat, weight: categoryWeight, products: [] });
        }
        groupMap.get(primaryCat).products.push(supp);
    });

    return Array.from(groupMap.values()).sort((a, b) => b.weight - a.weight);
}

/**
 * Apply the recommendation weight cutoff to sorted category groups:
 *   - below hardFloorRatio of the top weight → always dropped (hard floor)
 *   - at or above softCeilingRatio of the top weight → always kept
 *   - between the two → kept only while in the top `topN`
 *
 * The cutoff ratios are config-driven (RECOMMENDATION_DISPLAY.protocol) so the
 * number of generated categories that surface is tunable without touching code.
 * Callers may override any ratio by passing a partial `ratios` object; omitted
 * keys fall back to the config. The defaults reproduce the previous hardcoded
 * behavior (0.30 / 0.50 / top 3) exactly.
 *
 * @param {Array} sortedGroups - Output of groupSupplementsByCategory
 * @param {Object} [ratios] - Optional override { hardFloorRatio, softCeilingRatio, topN }
 * @returns {Array} Filtered groups
 */
export function applyCategoryCutoff(sortedGroups, ratios = {}) {
    if (!sortedGroups.length) return sortedGroups;

    const {
        hardFloorRatio = RECOMMENDATION_DISPLAY.protocol.hardFloorRatio,
        softCeilingRatio = RECOMMENDATION_DISPLAY.protocol.softCeilingRatio,
        topN = RECOMMENDATION_DISPLAY.protocol.topN,
    } = ratios;

    const maxWeight = sortedGroups[0].weight;
    const hardFloor = maxWeight * hardFloorRatio;
    const softCeiling = maxWeight * softCeilingRatio;

    return sortedGroups.filter((group, i) => {
        if (group.weight < hardFloor) return false;
        return group.weight >= softCeiling || i < topN;
    });
}

/**
 * Whether an entry survives a recommendation regeneration.
 * Generated entries (source === 'generated') are replaced by fresh engine
 * output; everything else (manual additions, interventions) is preserved.
 *
 * Provenance/regenerate classification is SOURCE-based. Per-entry physician
 * LOCK/edit/remove gating is AUTHOR-based and lives in protocolPermissions.js;
 * regeneration intentionally does not consult locks here — locked generated
 * entries are still refreshed, and the editor reconciles author/order on save.
 */
export function shouldPreserveOnRegenerate(entry) {
    return entry.source !== 'generated';
}

/**
 * Merge freshly generated entries into an existing protocol's entries.
 * Generated entries are replaced wholesale; preserved entries (see
 * shouldPreserveOnRegenerate) keep their position after them. On a name
 * collision the preserved entry wins — it carries user/doctor intent.
 *
 * @param {Array|null} existingEntries - Current protocol.json entries
 * @param {Array} generatedEntries - Fresh output of recommendationsToEntries
 * @returns {Array} Merged entries
 */
export function mergeRegeneratedEntries(existingEntries, generatedEntries) {
    const preserved = (existingEntries || []).filter(shouldPreserveOnRegenerate);
    const preservedNames = new Set(preserved.map(e => e.name));
    const deduped = generatedEntries.filter(e => !preservedNames.has(e.name));
    return [...deduped, ...preserved];
}

/**
 * Convert engine-generated recommendations (supplements array) into protocol entries.
 * Each supplement becomes one entry with resolved dose information.
 *
 * Each entry is stamped with `source: 'generated'` (provenance: engine-generated)
 * and `author` set to the acting role of whoever triggered generation. A fresh
 * `addedAt` anchors per-item compliance windows (items added later clip to their
 * own date).
 *
 * @param {Array} supplements - recommendations.supplements from the engine
 * @param {string} mode - 'support' or 'therapeutic'
 * @param {string} [authorRole='patient'] - Acting role ('doctor' | 'patient')
 * @returns {Array} Flat array of protocol entries (each { source: 'generated', author, addedAt })
 */
export function recommendationsToEntries(supplements, mode, authorRole = 'patient') {
    if (!supplements || !supplements.length) return [];

    // Group by primary category, pick the first product per category
    const seen = new Map();
    supplements.forEach(supp => {
        const primaryCat = supp.fulfills_categories?.[0]?.category
            || supp.category
            || 'uncategorized';
        if (!seen.has(primaryCat)) {
            seen.set(primaryCat, supp);
        }
    });

    return Array.from(seen.values()).map(supp => {
        const productName = getProductName(supp);

        const details = resolveProductDetails(supp);
        const weight = supp.total_weight || 0;
        const doseResult = calculateDose(details, weight, mode);

        return {
            name: productName,
            category: supp.fulfills_categories?.[0]?.category || supp.category || 'uncategorized',
            dose: doseResult?.dose || 1,
            frequency: doseResult?.frequency || 1,
            unit: details.unit || 'capsule',
            freqUnit: details.freq_unit || 'day',
            emptyStomach: details.empty_stomach || false,
            instructions: null,
            notes: null,
            source: 'generated',
            author: authorRole === 'doctor' ? 'doctor' : 'patient',
            // Anchors per-item compliance windows (items added later clip to their own date)
            addedAt: new Date().toISOString()
        };
    });
}

/**
 * Group entries by category for display.
 *
 * @param {Array} entries - Protocol entries
 * @returns {Array} Array of { category, entries } groups
 */
export function entriesToDisplayGroups(entries) {
    if (!entries || !entries.length) return [];

    const groupMap = new Map();
    entries.forEach(entry => {
        const cat = entry.category || 'uncategorized';
        if (!groupMap.has(cat)) {
            groupMap.set(cat, []);
        }
        groupMap.get(cat).push(entry);
    });

    return Array.from(groupMap.entries()).map(([category, items]) => ({
        category,
        entries: items
    }));
}

/**
 * Create a new entry from a supplements catalog item.
 *
 * Manual catalog additions are stamped with `source: 'manual'` (provenance) and
 * `author` set to the acting role. A fresh `addedAt` anchors the item's
 * compliance window.
 *
 * @param {string} productName - Key in supplements.json
 * @param {Object} catalogEntry - The catalog entry object
 * @param {string} [authorRole='patient'] - Acting role ('doctor' | 'patient')
 * @returns {Object} Protocol entry ({ source: 'manual', author, addedAt })
 */
export function catalogToEntry(productName, catalogEntry, authorRole = 'patient') {
    return {
        name: productName,
        category: catalogEntry.category?.primary?.[0] || 'uncategorized',
        dose: catalogEntry.min_daily_dose || 1,
        frequency: 1,
        unit: catalogEntry.unit || 'capsule',
        freqUnit: catalogEntry.freq_unit || 'day',
        emptyStomach: catalogEntry.empty_stomach || false,
        instructions: null,
        notes: null,
        source: 'manual',
        author: authorRole === 'doctor' ? 'doctor' : 'patient',
        addedAt: new Date().toISOString()
    };
}

/**
 * Migrate a single entry to the orthogonal { author, source } model used here.
 * Returns a clone; the input entry is never mutated. Idempotent: re-running an
 * already-migrated entry passes it through unchanged.
 *
 * Each output entry carries:
 *  - `author`: 'doctor' | 'patient' — WHO is responsible (drives lock/edit/remove
 *    via protocolPermissions.js)
 *  - `source`: 'generated' | 'manual' — HOW it was created (provenance; drives
 *    regenerate classification via shouldPreserveOnRegenerate)
 *
 * Source vocabulary is #257's: only 'generated' and 'manual'. Chat/LLM
 * provenance is handled by #257's flow; this migration never emits 'llm' or the
 * interim 'backend' — both map to 'generated'.
 *
 * Migration table:
 *  (a) entry already has a valid author AND a valid source -> passthrough
 *  (b) interim source 'backend'   -> source 'generated', author from generatedBy
 *  (c) interim source 'doctor'    -> author 'doctor',  source 'generated'
 *  (d) interim source 'patient'   -> author 'patient', source 'generated'
 *  (e) interim source 'llm' (w/ actingAs) -> author from actingAs,
 *      source 'generated' (actingAs dropped from output)
 *  (f) source 'generated'         -> source 'generated', author from generatedBy
 *  (g) legacy source 'manual'|'custom' -> source 'manual', author from generatedBy
 *  (h) unknown/missing source     -> source 'manual', author from generatedBy
 *
 * In (b),(f),(g),(h) author derives from generatedBy.userType
 * ('doctor' -> 'doctor', otherwise 'patient').
 *
 * @param {Object} entry - A protocol entry
 * @param {Object} [protocol] - The protocol envelope (for generatedBy.userType)
 * @returns {Object} A new entry with { author, source }
 */
export function normalizeEntrySource(entry, protocol) {
    if (!entry) return entry;

    const VALID_AUTHORS = ['doctor', 'patient'];
    const VALID_SOURCES = ['generated', 'manual'];
    const authorFromProtocol = () =>
        (protocol?.generatedBy?.userType === 'doctor' ? 'doctor' : 'patient');

    // (a) Already migrated: both fields valid -> passthrough (idempotent).
    if (VALID_AUTHORS.includes(entry.author) && VALID_SOURCES.includes(entry.source)) {
        return { ...entry };
    }

    const clone = { ...entry };
    const src = clone.source;

    if (src === 'backend') {
        // (b) interim backend -> generated
        clone.source = 'generated';
        clone.author = authorFromProtocol();
    } else if (src === 'doctor') {
        // (c) interim doctor
        clone.author = 'doctor';
        clone.source = 'generated';
    } else if (src === 'patient') {
        // (d) interim patient
        clone.author = 'patient';
        clone.source = 'generated';
    } else if (src === 'llm') {
        // (e) interim llm -> generated; LLM provenance is no longer a source value
        clone.author = clone.actingAs === 'doctor' ? 'doctor' : 'patient';
        clone.source = 'generated';
        delete clone.actingAs;
    } else if (src === 'generated') {
        // (f) generated
        clone.source = 'generated';
        clone.author = authorFromProtocol();
    } else if (src === 'manual' || src === 'custom') {
        // (g) legacy manual/custom
        clone.source = 'manual';
        clone.author = authorFromProtocol();
    } else {
        // (h) unknown/missing source
        clone.source = 'manual';
        clone.author = authorFromProtocol();
    }

    return clone;
}

/**
 * Migrate every entry of a protocol through normalizeEntrySource.
 * Returns a new protocol object with a new entries array; does not mutate input.
 *
 * @param {Object} protocol - The protocol envelope
 * @returns {Object} The protocol with normalized entry sources
 */
export function normalizeProtocolEntries(protocol) {
    if (!protocol || !Array.isArray(protocol.entries)) return protocol;
    return {
        ...protocol,
        entries: protocol.entries.map(e => normalizeEntrySource(e, protocol))
    };
}

/**
 * Resolve product details from a supplement recommendation,
 * falling back to the catalog if inline details are incomplete.
 */
export function resolveProductDetails(supp) {
    const rawDetails = supp.product_details?.min_daily_dose != null
        ? supp.product_details
        : supp.products?.[0]?.product_details || supp.product_details || {};

    const productName = getProductName(supp);
    const catalogEntry = supplementsCatalog[productName];

    if (rawDetails.min_daily_dose != null) return rawDetails;

    return {
        ...(catalogEntry ? {
            min_daily_dose: catalogEntry.min_daily_dose,
            max_support_daily_dose: catalogEntry.max_support_daily_dose,
            max_therapeutic_daily_dose: catalogEntry.max_therapeutic_daily_dose,
            max_single_dose: catalogEntry.max_single_dose,
            unit: catalogEntry.unit,
            freq_unit: catalogEntry.freq_unit,
            empty_stomach: catalogEntry.empty_stomach,
        } : {}),
        ...rawDetails
    };
}

/**
 * Extract the display name from a supplement/product object.
 * Handles multiple data shapes from the recommendation engine.
 */
export function getProductName(product) {
    if (!product) return 'Unknown';
    return product.product_details?.product_name
        || product.name
        || product.supplement
        || product.products?.[0]?.name
        || 'Unknown';
}

/**
 * Return all supplement product names from the catalog.
 */
export function getSupplementNames() {
    return Object.keys(supplementsCatalog).filter(k => k !== '_metadata');
}

/**
 * Return catalog data for a specific supplement, or undefined.
 */
export function getSupplementData(name) {
    return supplementsCatalog[name];
}

/**
 * Return all unique category strings from primary + secondary arrays
 * across all catalog entries, sorted alphabetically.
 * Computed once at module load since the catalog is static.
 */
let _allCategories = null;
export function getAllCategories() {
    if (_allCategories) return _allCategories;
    const cats = new Set();
    for (const name of getSupplementNames()) {
        const entry = supplementsCatalog[name];
        const primary = entry?.category?.primary || [];
        const secondary = entry?.category?.secondary || [];
        for (const c of [...primary, ...secondary]) {
            cats.add(c);
        }
    }
    _allCategories = Array.from(cats).sort();
    return _allCategories;
}

/**
 * Return supplement names matching ALL selected categories (intersection).
 * Each supplement matches a category if it appears in its primary or secondary array.
 *
 * @param {string[]} categories - Selected category slugs
 * @param {string[]} existingNames - Names to exclude (already in protocol)
 * @returns {string[]} Matching supplement names
 */
export function getSupplementsByCategories(categories, existingNames = []) {
    if (!categories.length) return [];

    const existingSet = new Set(existingNames.map(n => n.toLowerCase()));

    return getSupplementNames().filter(name => {
        if (existingSet.has(name.toLowerCase())) return false;

        const entry = supplementsCatalog[name];
        const primary = entry?.category?.primary || [];
        const secondary = entry?.category?.secondary || [];
        const allCats = new Set([...primary, ...secondary]);

        return categories.every(cat => allCats.has(cat));
    });
}
